/**
 * Response parsing and validation utilities.
 * Shared utilities for detecting HTML responses, parsing JSON, and handling API errors.
 */

/**
 * Error message patterns that indicate an expired session.
 * Used to detect when API responses indicate authentication failure.
 */
export const EXPIRED_SESSION_ERROR_PATTERNS = [
  'Session expired',
  'expired session',
  // Note: We intentionally do NOT include bare 'Invalid data' here because:
  // 1. Third-party scripts (e.g., Crisp chat widget) throw bare "Invalid data" errors
  // 2. Our API client always adds context (e.g., "Invalid JSON in response (malformed JSON). Status: 200...")
  // 3. The more specific patterns below will catch our actual API errors
  'Invalid JSON in response',
  'HTML response instead of JSON',
  'Expected JSON response but received HTML',
] as const;

/**
 * Detects if a response text appears to be HTML.
 * Checks for various HTML patterns to catch redirect pages, login pages, etc.
 *
 * This is useful for detecting when an API returns HTML instead of JSON,
 * which commonly happens when:
 * - Session has expired and user is redirected to login
 * - Server returns an error page
 * - Network proxy returns an HTML error page
 *
 * @param text - Response text to check
 * @returns True if the response appears to be HTML, false otherwise
 *
 * @example
 * ```ts
 * const responseText = await response.text();
 * if (isHtmlResponse(responseText)) {
 *   throw new Error('Received HTML instead of JSON - session may have expired');
 * }
 * ```
 *
 * @see {@link ../utils/error-handling.ts} - Related error handling utilities
 */
export function isHtmlResponse(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;

  // Check for common HTML/XML document indicators at the start
  const htmlIndicators = [
    /^<!DOCTYPE\s+html/i,
    /^<html[\s>]/i,
    /^<\?xml/i, // XML declaration
    /^<!--/, // HTML comment
  ];

  if (htmlIndicators.some((pattern) => pattern.test(trimmed))) {
    return true;
  }

  // Check for HTML structure (opening and closing tags)
  // This catches cases where there's whitespace or BOM before the HTML
  const hasHtmlStructure =
    (trimmed.includes('<html') && trimmed.includes('</html>')) ||
    (trimmed.includes('<body') && trimmed.includes('</body>')) ||
    (trimmed.includes('<head') && trimmed.includes('</head>')) ||
    (trimmed.includes('<!DOCTYPE') && trimmed.includes('</html>'));

  if (hasHtmlStructure) {
    return true;
  }

  // Check for HTML tags at the start (catches partial HTML or fragments)
  if (trimmed.startsWith('<')) {
    return (
      trimmed.includes('<html') ||
      trimmed.includes('<!DOCTYPE') ||
      trimmed.includes('<head') ||
      trimmed.includes('<body') ||
      trimmed.includes('<div') ||
      trimmed.includes('<script')
    );
  }

  // Check for HTML patterns anywhere in the response
  // This catches cases where there's whitespace or BOM before the HTML
  if (trimmed.includes('<html') || trimmed.includes('<!DOCTYPE')) {
    return true;
  }

  // Note: We intentionally do NOT check for generic XML structures (like <root><item></item></root>)
  // because we only want to detect actual HTML responses, not generic XML.
  // The function already has comprehensive checks for HTML-specific tags and structures above.

  return false;
}
